import type { Store } from '../persistence/port';
import { importExam } from '../persistence/importExam';
import { toRelayDate } from '../persistence/relay';
import type { ExamRow } from '../persistence/rows';
import { RELAY_SEED_NS, uuidv5 } from '../platform/uuid';
import { zuBundle, type RelayDokument, type RelayPersonen } from '../server/dokument';
import { holeMedien } from '../server/medienAblage';
import { PreviewError, toBase64 } from './previewCrypto';
import type { PreviewMessage } from './previewHttp';

/** Was die Oberfläche nach dem Einspielen braucht, um gleich in den Bogen zu gehen. */
export interface InstallierteVorschau {
  examId: string;
  candidateId: string;
  examinerId: string;
}

/**
 * Der Platzhalter, wenn die Preview keinen Prüfer trägt — bei Relay der Normalfall
 * (`examiners=[]`: eine frisch importierte Prüfung hat keine). Ohne Prüfer startet Stage keine
 * Station (`confirmSetup`). Entscheidung des Nutzers (Weg C, 2026-10-25): Stage legt ihn selbst
 * an, nur in der eigenen Ablage, Relay tut nichts.
 *
 * Die Kennung ist aus der Prüfung abgeleitet: eine zweite Preview derselben Prüfung trifft
 * denselben Satz, statt einen weiteren daneben zu legen.
 */
export const platzhalterPruefer = (examId: string) => ({
  id: uuidv5(RELAY_SEED_NS, 'preview-examiner/' + examId),
  firstName: '',
  lastName: 'Vorschau',
  groupName: '',
});

/**
 * Spielt eine abgeholte Preview ein — denselben Weg wie ein Relay-Download (`zuBundle`,
 * `importExam`, `holeMedien`), damit sie zeigt, was der echte Download täte.
 *
 * ⛔ **Eine Preview ist immer simulativ**, auch wenn das Dokument es nicht sagt. Daran hängt der
 * Zaun in `server/sync.ts` und `server/anhaenge.ts`: ginge eine Zeile hinaus, stünde eine
 * Probebewertung in der echten Prüfungsdatenbank.
 *
 * ⚠️ **Eine echte Prüfung gleicher Kennung wird nie überschrieben.** Liegt sie ohne
 * `preview = 'Y'` da, bricht das hier ab, bevor etwas geschrieben ist. Eine ältere Preview
 * derselben Prüfung ersetzt `importExam` (es löscht anhand der Kennung zuerst).
 */
export async function installPreview(
  store: Store,
  message: PreviewMessage,
  jetzt: Date = new Date(),
  schreibe?: (pfad: string, base64: string) => Promise<void>,
): Promise<InstallierteVorschau> {
  const docFile = message.header.files.find((f) => f.metadata?.kind === 'document');
  const raw = docFile && message.files.get(docFile.id);
  if (!raw) throw new PreviewError('format', 'kein Dokument');
  let dokument: RelayDokument;
  try {
    dokument = JSON.parse(new TextDecoder().decode(raw)) as RelayDokument;
  } catch {
    throw new PreviewError('format', 'Dokument');
  }
  const examId = dokument?.exam?.id;
  if (!examId || dokument.candidates?.length !== 1) throw new PreviewError('format', 'Prüfung oder Prüfling fehlt');

  const [vorhanden] = await store.all<ExamRow>('Exam', { id: examId });
  if (vorhanden && vorhanden.preview !== 'Y') throw new PreviewError('realExam', examId);

  const personen: RelayPersonen = {
    // Ein Prüfer ohne Kennung fällt in `zuBundle` weg — er zählt hier also auch nicht.
    examiners: dokument.examiners?.some((p) => p.id) ? dokument.examiners : [platzhalterPruefer(examId)],
    actors: dokument.actors ?? [],
  };
  // Ohne `exam/available` kein Kopf: Art und Zeitfenster kommen von hier, wie bei Ink.
  const { bundle, medien } = zuBundle(
    dokument,
    {
      id: examId,
      examType: 'OSCE',
      simulative: true,
      startDate: toRelayDate(jetzt),
      startTime: '0000',
      endDate: null,
      endTime: '2359',
    },
    personen,
  );
  if (!bundle.candidates[0]) throw new PreviewError('format', 'Prüfling ohne Kennung');
  bundle.exam.simulative = 'Y';
  bundle.exam.preview = 'Y';
  await importExam(store, bundle);

  // Zugeordnet über `mediaID`, nicht über den Namen (Vertrag Relay). Der Medientyp kommt aus
  // dem Dokument — `zuBundle` hat ihn schon in `medien`.
  const bytesJeMedium = new Map<string, Uint8Array>();
  for (const f of message.header.files) {
    const mediaID = f.metadata?.kind === 'media' ? f.metadata.mediaID : undefined;
    const bytes = mediaID ? message.files.get(f.id) : undefined;
    if (mediaID && bytes) bytesJeMedium.set(mediaID, bytes);
  }
  await holeMedien(medien, {
    examId,
    hole: async (id) => {
      const bytes = bytesJeMedium.get(id);
      if (!bytes) throw new Error('nicht in der Preview');
      return toBase64(bytes);
    },
    schreibe,
  });

  return { examId, candidateId: bundle.candidates[0].id, examinerId: bundle.examExaminers[0].examinerID };
}
